import { customElement } from 'lit/decorators.js';
import { goURL, resolveURL } from '../../../../webmodule-common/other/ui/resource-resolver';
import { html, TemplateResult } from 'lit';
import { LangFabricationJobSummaryTable } from '../../../../localization/scripts/fabrication-jobs/views/fabrication-job-summary-table-lang';
import { DataTableWrapper } from '../../../../webmodule-common/other/ui/datatable-view';
import { RequestPage, ResultPaginated } from '../../../../webmodule-common/other/ui/RequestPage';
import { resourceFabricationJob } from '../../../fabrication-jobs/ui/launcher';
import { spaNavigate } from '../data/fabrication-purchase-order-helper-functions';
import {
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../../webmodule-common/components/src/webmodule-components';

// A linked job reduced to what the grid shows: identity plus the two visible fields.
export interface LinkedJobRow {
  id: string;
  jobNumber: number;
  title: string;
}

export interface FabricationPurchaseOrderLinkedJobsTableOptions {
  items: () => LinkedJobRow[];
}

// A compact, browser-style grid of the jobs linked to a PO: just the job number and
// title, each a link to the job. Rows are supplied in memory (the detail view fetches
// the linked jobs); there is no server paging.
@customElement('wm-fabricationpurchaseorderlinkedjobstable')
export class FabricationPurchaseOrderLinkedJobsTable extends DataTableWrapper<LinkedJobRow> {
  options: FabricationPurchaseOrderLinkedJobsTableOptions;

  constructor(options: FabricationPurchaseOrderLinkedJobsTableOptions) {
    super();
    this.options = options;
  }

  override disablePagination(): boolean {
    return true;
  }

  async getRowsFromServer(_request: RequestPage): Promise<ResultPaginated<LinkedJobRow>> {
    const items = this.options.items();
    return {
      count: items.length,
      pageCount: 1,
      pageIndex: 0,
      pageSize: items.length,
      results: items
    };
  }

  getColumns(): WebModuleTableColumnDef[] {
    // Column sizing moves from Bootstrap `colpx-*` / `colpxmax-*` classes (which
    // don't pierce the new table's shadow DOM) to the column's own width model.
    return [
      {
        title: () => LangFabricationJobSummaryTable.jobNumberTitle(),
        fieldName: 'jobNumber',
        width: '90px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const row = item as LinkedJobRow;
          return this.jobLink(row, row.jobNumber.toString());
        }
      },
      {
        title: () => LangFabricationJobSummaryTable.titleTitle(),
        fieldName: 'title',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const row = item as LinkedJobRow;
          return this.jobLink(row, row.title || LangFabricationJobSummaryTable.untitled());
        }
      }
    ];
  }

  // Mirrors the job browser's link: a client-side (SPA) navigation to the job detail page.
  private jobLink(row: LinkedJobRow, value: string): TemplateResult {
    return html`<a
      class="fabrication-job-link"
      href="${resolveURL(resourceFabricationJob, row.id)}"
      @click=${(e: MouseEvent) => spaNavigate(e, () => goURL(resourceFabricationJob, row.id))}
      >${value}</a
    >`;
  }
}
